import { alpha, createTheme, type Theme } from "@mui/material/styles"

// ---------------------------------------------------------------------------
// Default MUI theme (toggle-able via debug dialog)
// ---------------------------------------------------------------------------

export const defaultTheme: Theme = createTheme({
  palette: {
    mode: "light",
  },
})

// ---------------------------------------------------------------------------
// Mobilus Visual Identity — Color Tokens
// ---------------------------------------------------------------------------

// Primary — Rich Teal
const primary = {
  main: "#0B7285",
  dark: "#065A6D",
  light: "#E8F6F8",
}

// Accent — Warm Coral (mapped to MUI "secondary")
const accent = {
  main: "#F08C6A",
  dark: "#D9734F",
  light: "#FFF1EC",
}

// Neutrals — Teal-Tinted Grays
const neutral = {
  50: "#F7FAFA",
  100: "#EEF3F4",
  200: "#DCE4E6",
  300: "#B8C8CC",
  400: "#8DA0A6",
  500: "#627880",
  600: "#465B63",
  700: "#334950",
  800: "#1E3038",
  900: "#0F1A1F",
  dark: "#1A2A35",
  darkLight: "#2D3E4A",
}

// ---------------------------------------------------------------------------
// Typography — Font Families
// ---------------------------------------------------------------------------

const headingFamily = "'Plus Jakarta Sans Variable', 'Plus Jakarta Sans', sans-serif"
const bodyFamily = "'Inter Variable', 'Inter', sans-serif"

// ---------------------------------------------------------------------------
// Theme
// ---------------------------------------------------------------------------

export const lightTheme = createTheme({
  palette: {
    mode: "light",
    primary,
    secondary: accent,
    error: { main: "#E53935" },
    warning: { main: "#FF9800" },
    success: { main: "#43A047" },
    info: { main: primary.main },
    background: {
      default: neutral[50],
      paper: "#FFFFFF",
    },
    text: {
      primary: neutral.dark,
      secondary: neutral[500],
      disabled: neutral[300],
    },
    divider: neutral[200],
    grey: {
      50: neutral[50],
      100: neutral[100],
      200: neutral[200],
      300: neutral[300],
      400: neutral[400],
      500: neutral[500],
      600: neutral[600],
      700: neutral[700],
      800: neutral[800],
      900: neutral[900],
    },
    action: {
      hover: alpha(primary.main, 0.06),
      selected: alpha(primary.main, 0.1),
      focus: alpha(primary.main, 0.12),
    },
  },

  typography: {
    fontFamily: bodyFamily,
    h1: {
      fontFamily: headingFamily,
      fontWeight: 800,
      lineHeight: 1.1,
      color: neutral.dark,
    },
    h2: {
      fontFamily: headingFamily,
      fontWeight: 700,
      lineHeight: 1.1,
      color: neutral.dark,
    },
    h3: {
      fontFamily: headingFamily,
      fontWeight: 600,
      lineHeight: 1.2,
      color: neutral.dark,
    },
    h4: {
      fontFamily: headingFamily,
      fontWeight: 600,
      lineHeight: 1.2,
      color: neutral.dark,
    },
    h5: {
      fontFamily: headingFamily,
      fontWeight: 600,
      lineHeight: 1.25,
      color: neutral.dark,
    },
    h6: {
      fontFamily: headingFamily,
      fontWeight: 600,
      lineHeight: 1.3,
      color: neutral.dark,
    },
    subtitle1: {
      fontFamily: bodyFamily,
      fontWeight: 500,
      color: neutral[600],
    },
    subtitle2: {
      fontFamily: bodyFamily,
      fontWeight: 500,
      color: neutral[600],
    },
    body1: {
      fontFamily: bodyFamily,
      fontWeight: 400,
      lineHeight: 1.65,
      color: neutral[500],
    },
    body2: {
      fontFamily: bodyFamily,
      fontWeight: 400,
      lineHeight: 1.6,
      color: neutral[500],
    },
    button: {
      fontFamily: bodyFamily,
      fontWeight: 600,
      textTransform: "none",
    },
    caption: {
      fontFamily: bodyFamily,
      fontWeight: 400,
      color: neutral[400],
    },
    overline: {
      fontFamily: bodyFamily,
      fontWeight: 600,
      letterSpacing: "0.08em",
    },
  },

  shape: {
    borderRadius: 12,
  },

  shadows: [
    "none",
    // 1 — subtle card border-like shadow
    `0 1px 2px ${alpha(neutral[900], 0.05)}`,
    // 2 — resting card
    `0 1px 4px ${alpha(neutral[900], 0.07)}`,
    // 3
    `0 2px 6px ${alpha(neutral[900], 0.08)}`,
    // 4 — hover card lift
    `0 4px 12px ${alpha(neutral[900], 0.1)}`,
    // 5
    `0 6px 16px ${alpha(neutral[900], 0.1)}`,
    // 6
    `0 8px 24px ${alpha(neutral[900], 0.12)}`,
    // 7
    `0 10px 28px ${alpha(neutral[900], 0.12)}`,
    // 8 — dialogs / drawers
    `0 12px 32px ${alpha(neutral[900], 0.14)}`,
    // 9–24: progressive
    ...Array.from({ length: 16 }, (_, i) => {
      const v = 14 + i * 2
      return `0 ${v}px ${v + 8}px ${alpha(neutral[900], 0.1 + i * 0.005)}`
    }),
  ] as unknown as import("@mui/material/styles").Shadows,

  components: {
    // -- CssBaseline -------------------------------------------------------
    MuiCssBaseline: {
      styleOverrides: `
        body {
          background-color: ${neutral[50]};
        }
        @media print {
          body {
            background-color: #fff !important;
          }
          /* Hide all app chrome when printing labels */
          .no-print,
          header,
          nav,
          .MuiAppBar-root {
            display: none !important;
          }
          .print-label-container {
            display: block !important;
          }
          .print-label {
            page-break-after: always;
            page-break-inside: avoid;
          }
          .print-label:last-child {
            page-break-after: auto;
          }
        }
        @page {
          margin: 10mm;
          size: auto;
        }
      `,
    },

    // -- Buttons -----------------------------------------------------------
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 999,
          padding: "8px 20px",
          fontWeight: 600,
        },
        sizeSmall: {
          padding: "5px 14px",
          fontSize: "0.8125rem",
        },
        sizeLarge: {
          padding: "12px 28px",
          fontSize: "1rem",
        },
        containedSecondary: {
          color: "#FFFFFF",
          "&:hover": {
            backgroundColor: accent.dark,
          },
        },
        outlinedPrimary: {
          borderColor: alpha(primary.main, 0.4),
          "&:hover": {
            borderColor: primary.main,
            backgroundColor: alpha(primary.main, 0.06),
          },
        },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          "&:hover": {
            backgroundColor: alpha(primary.main, 0.08),
          },
        },
      },
    },

    MuiFab: {
      styleOverrides: {
        root: {
          boxShadow: `0 4px 14px ${alpha(primary.main, 0.3)}`,
        },
      },
    },

    // -- Cards -------------------------------------------------------------
    MuiCard: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: `1px solid ${neutral[100]}`,
          transition: "box-shadow 250ms ease, transform 250ms ease",
          "&:hover": {
            boxShadow: `0 4px 12px ${alpha(neutral[900], 0.1)}`,
            transform: "translateY(-2px)",
          },
        },
      },
    },

    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          backgroundImage: "none", // remove MUI default gradient overlay
        },
        rounded: {
          borderRadius: 16,
        },
      },
    },

    // -- Chips / Badges ----------------------------------------------------
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          fontWeight: 500,
        },
        filledPrimary: {
          backgroundColor: primary.light,
          color: primary.main,
        },
        filledSecondary: {
          backgroundColor: accent.light,
          color: accent.main,
        },
      },
    },

    // -- Dialogs -----------------------------------------------------------
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
        },
      },
    },

    // -- Drawer ------------------------------------------------------------
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: "none",
          boxShadow: `4px 0 16px ${alpha(neutral[900], 0.08)}`,
        },
      },
    },

    // -- AppBar ------------------------------------------------------------
    MuiAppBar: {
      defaultProps: {
        elevation: 0,
        color: "inherit",
      },
      styleOverrides: {
        root: {
          backgroundColor: "#FFFFFF",
          borderBottom: `1px solid ${neutral[200]}`,
        },
      },
    },

    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: "56px !important",
        },
      },
    },

    // -- Inputs ------------------------------------------------------------
    MuiTextField: {
      defaultProps: {
        variant: "outlined",
        size: "small",
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: primary.main,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderWidth: 2,
          },
        },
        notchedOutline: {
          borderColor: neutral[200],
        },
      },
    },

    // -- Tabs --------------------------------------------------------------
    MuiTab: {
      styleOverrides: {
        root: {
          fontFamily: bodyFamily,
          fontWeight: 500,
          textTransform: "none" as const,
          minHeight: 44,
        },
      },
    },

    // -- Tooltip -----------------------------------------------------------
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: neutral[800],
          borderRadius: 8,
          fontSize: "0.75rem",
          padding: "6px 12px",
        },
      },
    },

    // -- Snackbar ----------------------------------------------------------
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },

    // -- List items --------------------------------------------------------
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          "&.Mui-selected": {
            backgroundColor: alpha(primary.main, 0.1),
            "&:hover": {
              backgroundColor: alpha(primary.main, 0.15),
            },
          },
        },
      },
    },

    // -- Divider -----------------------------------------------------------
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: neutral[100],
        },
      },
    },
  },
})
